// Pure personal-finance date and projection helpers. Kept independent of React
// so invalid plans cannot leak absurd dates into the UI.
(function personalFinanceFuture(global) {
  const MAX_PROJECTION_MONTHS = 1200;
  const finite = (value, fallback = 0) => Number.isFinite(Number(value)) ? Number(value) : fallback;

  function goalMonths(goal = {}, extraMonthly = 0) {
    const remaining = Math.max(0, Math.max(0, finite(goal.target)) - Math.max(0, finite(goal.saved)));
    if (!remaining) return 0;
    const monthly = Math.max(0, finite(goal.monthly) + finite(extraMonthly));
    return monthly > 0 ? Math.ceil(remaining / monthly) : null;
  }

  function dateAfterMonths(monthsFromNow, startDate = new Date()) {
    const months = Number(monthsFromNow);
    if (!Number.isFinite(months) || months < 0 || months > MAX_PROJECTION_MONTHS) return null;
    const start = new Date(startDate);
    if (Number.isNaN(start.getTime())) return null;
    return new Date(start.getFullYear(), start.getMonth() + Math.round(months), 1);
  }

  function goalStatus(goal = {}, extraMonthly = 0, startDate = new Date()) {
    const months = goalMonths(goal, extraMonthly);
    if (months === 0) return { code: 'reached', date: new Date(startDate), months: 0 };
    if (months === null) return { code: 'unreachable', date: null, months: null };
    const date = dateAfterMonths(months, startDate);
    return date ? { code: 'planned', date, months } : { code: 'too-long', date: null, months };
  }

  function retirementMonths({ birthDate = '', currentAge = null, retireAge = 67, startDate = new Date() } = {}) {
    const targetAge = finite(retireAge, 67);
    if (targetAge < 40 || targetAge > 100) return null;
    if (birthDate) {
      const birth = new Date(`${String(birthDate).slice(0, 10)}T00:00:00`);
      const start = new Date(startDate);
      if (Number.isNaN(birth.getTime()) || Number.isNaN(start.getTime()) || birth >= start) return null;
      const target = new Date(birth.getFullYear() + targetAge, birth.getMonth(), 1);
      return Math.max(0, (target.getFullYear() - start.getFullYear()) * 12 + target.getMonth() - start.getMonth());
    }
    const age = finite(currentAge, -1);
    return age >= 0 && targetAge > age ? Math.round((targetAge - age) * 12) : null;
  }

  function project(startValue, monthly, months, annualReturn = 0, events = [], retireMonth = Infinity, draw = 0) {
    const horizon = Math.max(0, Math.min(MAX_PROJECTION_MONTHS, Math.round(finite(months))));
    const rate = Math.pow(1 + Math.max(-0.99, finite(annualReturn) / 100), 1 / 12) - 1;
    const out = [finite(startValue)];
    let value = out[0];
    for (let month = 1; month <= horizon; month += 1) {
      let contribution = month <= finite(retireMonth, Infinity) ? finite(monthly) : -Math.max(0, finite(draw));
      (Array.isArray(events) ? events : []).filter((event) => event?.active !== false).forEach((event) => {
        const start = Math.max(0, finite(event?.start));
        if (month === start) value -= finite(event?.oneTime);
        const duration = Math.max(0, finite(event?.duration));
        if (month >= start && (!duration || month < start + duration)) contribution += finite(event?.monthly);
      });
      value = value * (1 + rate) + contribution;
      out.push(Number.isFinite(value) ? Math.max(0, value) : 0);
    }
    return out;
  }

  global.PoFutureMath = { MAX_PROJECTION_MONTHS, dateAfterMonths, goalMonths, goalStatus, project, retirementMonths };
}(globalThis));
